import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { AlertTriangle } from "lucide-react";
import { authAPI } from "../api/authApi";
import {
  updateFileVisibility,
  type FileRecord,
  type FileVisibility,
} from "../api/file";
import { resolveLabwickUploadUrl } from "../utils/pictureUtils";

interface FileSharingEditorDialogProps {
  file: FileRecord | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSaved: (file: FileRecord) => void;
}

export function FileSharingEditorDialog({
  file,
  open,
  onOpenChange,
  onSaved,
}: FileSharingEditorDialogProps) {
  const [groups, setGroups] = useState<Array<{ uid: string; title: string }>>([]);
  const [loadingGroups, setLoadingGroups] = useState(false);
  const [visibility, setVisibility] = useState<FileVisibility>("private");
  const [selectedGroups, setSelectedGroups] = useState<string[]>([]);
  const [isPublic, setIsPublic] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const displayName = file?.original || file?.name?.split("/").pop() || file?.uid;
  const publicUrl = file?.name ? resolveLabwickUploadUrl(file.name) : "";

  useEffect(() => {
    if (!open || !file) return;
    setVisibility(file.visibility === "shared" ? "shared" : "private");
    // Seed from the file's groups (multi) or its legacy single group.
    const initial = Array.isArray(file.groups) && file.groups.length > 0
      ? file.groups
      : (file.group ? [file.group] : []);
    setSelectedGroups(initial);
    setIsPublic(file.public !== false);
    setError(null);

    let cancelled = false;
    setLoadingGroups(true);
    authAPI.getMyGroups().then((g) => {
      if (cancelled) return;
      setGroups(g);
      setLoadingGroups(false);
    });
    return () => {
      cancelled = true;
    };
  }, [open, file]);

  const toggleGroup = (uid: string, checked: boolean) => {
    setSelectedGroups((prev) =>
      checked ? [...new Set([...prev, uid])] : prev.filter((g) => g !== uid)
    );
  };

  const effectiveVisibility: FileVisibility =
    visibility === "shared" && selectedGroups.length > 0 ? "shared" : "private";

  const handleSave = async () => {
    if (!file) return;
    setSaving(true);
    setError(null);
    try {
      const shareGroups = effectiveVisibility === "shared" ? selectedGroups : [];
      const sharing = {
        visibility: effectiveVisibility,
        groups: shareGroups,
        group: shareGroups[0] ?? null,
        public: isPublic,
      };
      const res = await updateFileVisibility(file.uid, sharing);
      if (res?.success && res.file) {
        onSaved(res.file);
        onOpenChange(false);
      } else {
        setError(res?.message || "Failed to update sharing settings");
      }
    } catch (err) {
      console.error("Failed to update file sharing:", err);
      setError("Failed to update sharing settings");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Sharing settings</DialogTitle>
          <DialogDescription>
            Update who can see{" "}
            {displayName ? <span className="font-medium">{displayName}</span> : "this file"}{" "}
            within Labwick and whether the file link works without signing in.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="space-y-2">
            <Label>Visibility in Labwick</Label>
            <Select value={visibility} onValueChange={(v: FileVisibility) => setVisibility(v)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="private">Keep private (only me)</SelectItem>
                <SelectItem value="shared" disabled={!loadingGroups && groups.length === 0}>
                  Share with a group
                </SelectItem>
              </SelectContent>
            </Select>
          </div>

          {visibility === "shared" && (
            <div className="space-y-2">
              <Label>Groups</Label>
              {loadingGroups ? (
                <p className="text-sm text-muted-foreground">Loading groups…</p>
              ) : groups.length === 0 ? (
                <p className="text-sm text-muted-foreground">No groups available.</p>
              ) : (
                <div className="space-y-2 rounded-md border p-3">
                  {groups.map((g) => (
                    <label key={g.uid} className="flex items-center gap-2 cursor-pointer">
                      <Checkbox
                        checked={selectedGroups.includes(g.uid)}
                        onCheckedChange={(c) => toggleGroup(g.uid, c === true)}
                      />
                      <span className="text-sm">{g.title}</span>
                    </label>
                  ))}
                </div>
              )}
            </div>
          )}

          <div className="space-y-2">
            <label className="flex items-center gap-2 cursor-pointer">
              <Checkbox
                checked={isPublic}
                onCheckedChange={(c) => setIsPublic(c === true)}
              />
              <span className="text-sm font-medium">Publicly accessible</span>
            </label>
            {isPublic ? (
              <div className="rounded-md border border-amber-200 bg-amber-50 p-3 dark:border-amber-900/60 dark:bg-amber-950/40">
                <p className="flex items-start gap-1 text-xs text-amber-700 dark:text-amber-300">
                  <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                  <span>
                    Anyone with the link can open this file
                    {publicUrl ? (
                      <>
                        {" "}
                        at{" "}
                        <a
                          href={publicUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="break-all font-mono text-blue-600 hover:underline dark:text-blue-400"
                        >
                          {publicUrl}
                        </a>
                      </>
                    ) : null}{" "}
                    even without signing in.
                  </span>
                </p>
              </div>
            ) : null}
          </div>

          {error ? <p className="text-sm text-destructive">{error}</p> : null}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={saving || (visibility === "shared" && selectedGroups.length === 0)}
          >
            {saving ? "Saving…" : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default FileSharingEditorDialog;
